import React, { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { DocumentUploadCard } from "../ui/DocumentUploadCard";
import { UploadedFiles } from "../UploadedFiles";
import { InvalidRequirementReason } from "../InvalidRequirementReason";
import { CancelToReview } from "../CancelToReview";
import { Status } from "../../../redux/interfaces/folder.types";
import { toast } from "sonner";
import { uploadFileRequirement } from "../../../utils/vault/uploadFileRequirement";
import { useUploadFileToVault } from "../../../hooks/vault/mutations/useUploadFileToVault";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import { Loader2, Plus, Save, X } from "lucide-react";
import { Button } from "../../ui/button";
import { cn } from "../../../lib/utils";
import type { UploadFile } from "antd/es/upload/interface";
import { useLocation } from "react-router-dom";
import {
  createDocumentMetadataState,
  DocumentMetadataFields,
  mapDocumentMetadataFormValues,
  validateDocumentMetadata,
  type DocumentMetadataState,
} from "./DocumentMetadataFields";
import {
  LARGE_DOCUMENT_MAX_FILE_SIZE_MB,
  exceedsUploadTotalBytes,
  formatUploadLimit,
} from "../../../utils/vault/verificationUploadLimits";
import { UploadProgress } from "../ui/UploadProgress";
import {
  getDocumentUploadPolicy,
  UPLOAD_POLICIES,
} from "../../../constants/uploadPolicies";

interface Props {
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
}

const MAX_FILES = UPLOAD_POLICIES.constitutiveAct.max_files;

export const BusinessAct: React.FC<Props> = ({ onRegisterSubmit }) => {
  const [fileSlots, setFileSlots] = useState<UploadFile[][]>([[]]);
  const [metadataState, setMetadataState] = useState<DocumentMetadataState>({
    values: {},
    requiredFields: {},
    displayFields: {},
    labelFields: {},
  });
  const [metadataErrors, setMetadataErrors] = useState<Record<string, string>>(
    {},
  );

  const dispatch = useAppDispatch();
  const location = useLocation();

  const { activeRequirement, activeVault, activeTab } = useAppSelector(
    (state) => state.vault,
  );
  const uploadMutation = useUploadFileToVault(activeVault?.id);
  const updateFormValuesMutation = useUpdateVaultFormValues(activeVault?.id);
  const uploadPolicy = getDocumentUploadPolicy({
    requirementSlug: activeRequirement?.slug,
    requirementStatus: activeRequirement?.status,
  });

  const enabledInputs =
    activeRequirement?.status === Status.PendingUpload ||
    activeRequirement?.status === Status.Invalid;
  // Admin panel (`/folders/:id`) needs to edit manual metadata regardless of
  // status; actor (`/vault/:id`) keeps the old enabledInputs gate.
  const isAdminPanel = !location.pathname.includes("vault");
  const showDocumentMetadata = isAdminPanel;
  const canEditDocumentMetadata = isAdminPanel || enabledInputs;
  const isAdminSubmitting =
    uploadMutation.isPending || updateFormValuesMutation.isPending;
  const initializedRequirementId = useRef<string | null>(null);

  // Initialize drafts once per requirement. Query refreshes may replace
  // form_values with an equivalent array and must not discard unsaved edits.
  useEffect(() => {
    const requirementId = activeRequirement?.id ?? null;
    if (initializedRequirementId.current === requirementId) return;
    initializedRequirementId.current = requirementId;
    setFileSlots([[]]);
    setMetadataState(
      createDocumentMetadataState(activeRequirement?.form_values || []),
    );
    setMetadataErrors({});
  }, [activeRequirement?.form_values, activeRequirement?.id]);

  // Clear file state when a requirement is rejected (Invalid) so the user must select new files
  useEffect(() => {
    if (activeRequirement?.status === Status.Invalid) {
      setFileSlots([[]]);
    }
  }, [activeRequirement?.status]);

  const handleMetadataChange = (
    key: string,
    value: string | undefined | null,
  ) => {
    setMetadataState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [key]: value,
      },
    }));
    setMetadataErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleMetadataBlur = async (key: string) => {
    const errors = await validateDocumentMetadata(
      activeRequirement?.form_values || [],
      metadataState,
    );
    setMetadataErrors((prev) => ({
      ...prev,
      [key]: errors[key],
    }));
  };

  const handleFileChange = (index: number, files: UploadFile[]) => {
    setFileSlots((prev) => {
      const newSlots = [...prev];
      newSlots[index] = files;
      return newSlots;
    });
  };

  const addFileSlot = () => {
    if (fileSlots.length < MAX_FILES) {
      setFileSlots((prev) => [...prev, []]);
    }
  };

  const removeFileSlot = (index: number) => {
    if (fileSlots.length > 1) {
      setFileSlots((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Count how many slots have files
  const filledSlotsCount = fileSlots.filter((slot) => slot.length > 0).length;
  const canAddMore = fileSlots.length < MAX_FILES && enabledInputs;

  const handleSubmit = async (): Promise<boolean> => {
    const nextMetadataErrors = showDocumentMetadata
      ? await validateDocumentMetadata(
          activeRequirement?.form_values || [],
          metadataState,
        )
      : {};
    setMetadataErrors(nextMetadataErrors);
    if (Object.keys(nextMetadataErrors).length > 0) {
      toast.error("Por favor revisa los datos del documento");
      return false;
    }

    const newFormValues = activeRequirement?.form_values
      ? mapDocumentMetadataFormValues(
          activeRequirement.form_values,
          metadataState.values,
        )
      : undefined;

    // Admin-only path (mirrors ID.tsx): when staff edits the manual fields
    // without attaching new files, persist just the form_values and keep the
    // current status. If staff does attach files, the actor flow below runs.
    if (isAdminPanel && filledSlotsCount === 0) {
      if (!activeRequirement || !newFormValues) return false;
      try {
        await updateFormValuesMutation.mutateAsync({
          reqId: activeRequirement.id,
          form_values: newFormValues,
          status: activeRequirement.status,
        });
        toast.success("Datos del documento actualizados correctamente.");
        setMetadataErrors({});
        return true;
      } catch (error) {
        console.error("Update failed:", error);
        return false;
      }
    }

    if (filledSlotsCount === 0) {
      toast.error("Por favor sube al menos un archivo");
      return false;
    }

    // Collect all files from all slots
    const formFiles = fileSlots
      .flat()
      .map((file) => file.originFileObj)
      .filter(Boolean);

    if (exceedsUploadTotalBytes(formFiles, uploadPolicy.max_total_bytes)) {
      toast.error(
        `El total de archivos no puede exceder ${formatUploadLimit(uploadPolicy.max_total_bytes)}`,
      );
      return false;
    }

    const documentTypeKey = "business_act";
    const requirementFiles = activeRequirement?.files || [];
    // formNFiles must match maxAllowedFiles[documentType] (which is 1).
    // The old Ant Design code counted form field names (always 1), not individual files.
    // uploadFileToVault handles sending all files in a single request.
    const formNFiles = 1;
    const successMessage = "Acta constitutiva subida correctamente.";

    const messageApi = {
      success: (msg: string) => toast.success(msg),
      error: (msg: string) => toast.error(msg),
    };

    try {
      await uploadFileRequirement({
        uploadMutateAsync: uploadMutation.mutateAsync,
        updateFormValuesMutateAsync: updateFormValuesMutation.mutateAsync,
        dispatch,
        requirementFiles,
        documentType: documentTypeKey,
        formNFiles,
        formFiles,
        formValues: {},
        activeRequirement,
        activeVault,
        activeTab,
        successMessage,
        reqFormValues: newFormValues,
        messageApi,
      });
      setFileSlots([[]]);
      setMetadataErrors({});
      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      return false;
    }
  };

  // Register submit function with parent for external triggering
  const submitFnRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  submitFnRef.current = handleSubmit;

  useEffect(() => {
    if (!onRegisterSubmit) return;
    const fn = async () => submitFnRef.current?.() ?? false;
    onRegisterSubmit(fn);
    return () => onRegisterSubmit(null);
  }, [onRegisterSubmit]);

  const tips = [
    "Documento legible y completo",
    "Todas las páginas deben ser visibles",
    "Incluir boleta de registro",
    "Puedes subir múltiples archivos si es necesario",
  ];

  return (
    <div className="w-full">
      {/* Description */}
      <p className="text-gray-600 mb-8">
        Adjunta el{" "}
        <span className="font-semibold text-gray-900">Acta Constitutiva</span>{" "}
        completa y con{" "}
        <span className="font-semibold text-gray-900">boleta de registro</span>{" "}
        de la <span className="font-semibold text-gray-900">empresa</span>.
      </p>

      {/* Invalid Requirement Reason — shown at the top so the rejection
          message is visible before the user scrolls through the form. */}
      <InvalidRequirementReason requirement={activeRequirement!} />

      {/* Two column layout - stack on mobile, 40/60 split on md+ */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 md:gap-8">
        {/* Left Column - Tips */}
        <div className="space-y-6 md:sticky md:top-0 md:self-start">
          {/* Tips */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Requisitos
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              {tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right Column - File Upload */}
        <div className="space-y-6">
          {showDocumentMetadata &&
            (activeRequirement?.form_values || []).length > 0 && (
              <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 sm:p-5">
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Datos del documento
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    Completa la información manual del acta antes de enviarla.
                  </p>
                </div>
                <DocumentMetadataFields
                  fields={activeRequirement?.form_values || []}
                  state={metadataState}
                  errors={metadataErrors}
                  onChange={handleMetadataChange}
                  onBlur={handleMetadataBlur}
                  disabled={!canEditDocumentMetadata}
                />
              </div>
            )}

          <div className="space-y-4">
            {/* File slots */}
            {fileSlots.map((files, index) => (
              <div key={index} className="relative">
                {/* Remove button for extra slots */}
                {fileSlots.length > 1 && enabledInputs && (
                  <button
                    type="button"
                    onClick={() => removeFileSlot(index)}
                    className={cn(
                      "absolute -top-2 -right-2 z-10",
                      "flex h-6 w-6 items-center justify-center",
                      "rounded-full bg-gray-100 border border-gray-200",
                      "text-gray-500 hover:text-red-600 hover:bg-red-50 hover:border-red-200",
                      "transition-colors",
                    )}
                    title="Eliminar"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}

                {/* Slot label */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {fileSlots.length > 1
                      ? `Documento ${index + 1}`
                      : "Acta constitutiva"}
                  </span>
                </div>

                <DocumentUploadCard
                  name={`business_act_${index}`}
                  label="Acta constitutiva completa"
                  type="both"
                  enableCropper={false}
                  maxSizeMB={LARGE_DOCUMENT_MAX_FILE_SIZE_MB}
                  onFileChange={(newFiles) => handleFileChange(index, newFiles)}
                  value={files}
                />
              </div>
            ))}

            {/* Add more button */}
            {canAddMore && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addFileSlot}
                className="w-full gap-2"
              >
                <Plus className="h-4 w-4" />
                Agregar otra página
              </Button>
            )}

            {/* Files counter */}
            {filledSlotsCount > 0 && (
              <p className="text-xs text-gray-500 text-center">
                {filledSlotsCount} de {fileSlots.length} documento
                {fileSlots.length > 1 ? "s" : ""} cargado
                {filledSlotsCount > 1 ? "s" : ""}
              </p>
            )}
          </div>

          <UploadProgress
            status={uploadMutation.status}
            progress={uploadMutation.uploadProgress}
            transport={uploadMutation.uploadTransport}
          />

          {/* Uploaded Files */}
          <UploadedFiles />

          {/* Admin update button — persists manual fields without changing status */}
          {isAdminPanel && (
            <div className="flex justify-start">
              <Button
                type="button"
                onClick={() => {
                  void handleSubmit();
                }}
                disabled={isAdminSubmitting}
                className="min-w-28"
              >
                {isAdminSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Actualizar
              </Button>
            </div>
          )}

          {/* Cancel to review (when already submitted) */}
          {!enabledInputs &&
            activeRequirement?.status === Status.PendingReview && (
              <CancelToReview requirement={activeRequirement} />
            )}
        </div>
      </div>
    </div>
  );
};
